import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Uses DOMPurify with a strict allowlist of safe tags and attributes.
 * 
 * USAGE: Replace any `dangerouslySetInnerHTML={{ __html: content }}`
 *        with `dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}`
 * 
 * @security This is a defense-in-depth measure. The primary XSS defense is:
 * - ReactMarkdown (used in chat) which does NOT render raw HTML
 * - No rehype-raw plugin is loaded, so markdown HTML tags are stripped
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
      'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'style', // Required for CSS keyframe injection in NovaPage.tsx
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'dir', 'style'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitize CSS-only content (for <style> tag injection).
 * Strips everything except CSS text content.
 */
export function sanitizeCss(css: string): string {
  // Remove any potential script injection via CSS expressions or url()
  return css
    .replace(/expression\s*\(/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/@import/gi, '');
}
